import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, clean } from "../../utils/sqlClient";

// Common responsibility parties offered in the per-item dropdown. Any distinct
// value already present in the checklist (e.g. custom entries or seeded
// defaults not on this list) is merged in as well, so nothing ever disappears.
const COMMON_PARTIES = ["CleanSpace", "Engineer of Record", "GC", "Owner", "Vendor", "By Others"];

// Color-code the two most meaningful poles; anything else renders neutral.
const RESP_COLORS = {
  "CleanSpace":         { bg: "#dbeafe", color: "#1e40af" },
  "Engineer of Record": { bg: "#ede9fe", color: "#5b21b6" },
};
function respColor(r) { return RESP_COLORS[r] || { bg: "#f3f4f6", color: "#374151" }; }

export default function BODChecklist({ projectId }) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [revisions, setRevisions]   = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  const [checklist, setChecklist]   = useState([]);
  const [rules, setRules]           = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterDiscipline, setFilterDiscipline] = useState("All");
  const [filterStatus, setFilterStatus]         = useState("All");
  const [filterResp, setFilterResp]             = useState("All");
  const [search, setSearch]         = useState("");

  // Load revisions + rules + milestones on mount
  useEffect(() => {
    Promise.all([
      sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`),
      sqlRead("EXEC sp_GetBODRules"),
      sqlRead(`EXEC sp_GetMilestones @project_id=${projectId}`),
    ]).then(([revs, r, m]) => {
      setRevisions(revs);
      setRules(r);
      setMilestones(m);
      // Default to most recent unlocked revision
      const active = revs.find(r => !r.IsLocked) || revs[0];
      if (active) setActiveRevId(active.RevisionID);
      else setLoading(false);
    }).catch(e => { console.error(e); setLoading(false); });
  }, [projectId]);

  // Load checklist when revision changes
  useEffect(() => {
    if (!activeRevId) return;
    setLoading(true);
    sqlRead(`EXEC sp_GetBODChecklist @revision_id=${activeRevId}`)
      .then(rows => setChecklist(rows))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeRevId]);

  // Build rule map: triggerItemID → [dependentItemIDs]
  const ruleMap = useMemo(() => {
    const map = {};
    rules.forEach(r => {
      if (!map[r.TriggerItemID]) map[r.TriggerItemID] = [];
      map[r.TriggerItemID].push(r.DependentItemID);
    });
    return map;
  }, [rules]);

  const activeRev = revisions.find(r => r.RevisionID === activeRevId);
  const isLocked  = activeRev?.IsLocked;

  const handleCheck = async (item, newChecked) => {
    if (isLocked) return;

    // Apply rules engine — cascade to dependent items
    const updates = [{ ItemID: item.ItemID, IsChecked: newChecked, IsAutoChecked: false }];
    if (newChecked && ruleMap[item.ItemID]) {
      ruleMap[item.ItemID].forEach(depId => {
        updates.push({ ItemID: depId, IsChecked: true, IsAutoChecked: true });
      });
    }

    // Optimistic UI update
    setChecklist(prev => prev.map(c => {
      const upd = updates.find(u => u.ItemID === c.ItemID);
      if (upd) return { ...c, IsChecked: upd.IsChecked, IsAutoChecked: upd.IsAutoChecked };
      return c;
    }));

    // Persist each update
    for (const upd of updates) {
      await sqlRead(`EXEC sp_SaveBODChecklistState
        @revision_id=${activeRevId},
        @item_id=${upd.ItemID},
        @is_checked=${upd.IsChecked ? 1 : 0},
        @is_auto_checked=${upd.IsAutoChecked ? 1 : 0},
        @updated_by='${clean(user?.name || "")}'`).catch(console.error);
    }
  };

  const handleExclude = async (item) => {
    if (isLocked) return;
    const newExcluded = !item.IsExcluded;
    setChecklist(prev => prev.map(c => c.ItemID === item.ItemID ? { ...c, IsExcluded: newExcluded } : c));
    await sqlRead(`EXEC sp_SaveBODChecklistState
      @revision_id=${activeRevId},
      @item_id=${item.ItemID},
      @is_checked=${item.IsChecked ? 1 : 0},
      @is_excluded=${newExcluded ? 1 : 0},
      @updated_by='${clean(user?.name || "")}'`).catch(console.error);
  };

  const handleResponsibility = async (item, newResp) => {
    if (isLocked) return;
    setChecklist(prev => prev.map(c => c.ItemID === item.ItemID ? { ...c, Responsibility: newResp } : c));
    await sqlRead(`EXEC sp_SaveBODChecklistState
      @revision_id=${activeRevId},
      @item_id=${item.ItemID},
      @responsibility='${clean(newResp)}',
      @updated_by='${clean(user?.name || "")}'`).catch(console.error);
  };

  // Derived stats
  const disciplines = ["All", ...new Set(checklist.map(c => c.Discipline))].filter(Boolean);
  const respOptions = useMemo(() => {
    const set = new Set(COMMON_PARTIES);
    checklist.forEach(c => { if (c.Responsibility) set.add(c.Responsibility); });
    return Array.from(set);
  }, [checklist]);
  const activeItems = checklist.filter(c => !c.IsExcluded);
  const checkedCount = activeItems.filter(c => c.IsChecked).length;
  const totalCount   = activeItems.length;
  const pct = totalCount > 0 ? Math.round(checkedCount / totalCount * 100) : 0;

  // Filtered view
  const filtered = checklist.filter(c => {
    if (filterDiscipline !== "All" && c.Discipline !== filterDiscipline) return false;
    if (filterStatus === "Checked"   && !c.IsChecked)  return false;
    if (filterStatus === "Unchecked" && (c.IsChecked || c.IsExcluded)) return false;
    if (filterStatus === "Excluded"  && !c.IsExcluded) return false;
    if (filterResp !== "All" && (c.Responsibility || "") !== (filterResp === "(Unassigned)" ? "" : filterResp)) return false;
    if (search && !c.ItemName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by discipline
  const grouped = {};
  filtered.forEach(c => {
    if (!grouped[c.Discipline]) grouped[c.Discipline] = [];
    grouped[c.Discipline].push(c);
  });

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading checklist…</div>;

  if (revisions.length === 0) return (
    <div style={{ maxWidth: 560 }}>
      <div className="panel-title">BOD Checklist</div>
      <div className="panel-sub">No revisions yet. Create a revision from the Revisions tab to start the checklist.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: "none" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="panel-title">BOD Checklist</div>
          <div className="panel-sub">One checklist for every delivery method — the responsibility column is what adapts. Defaults come from the project's delivery method and are editable per item.</div>
        </div>

        {/* Revision selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>Revision:</span>
          <select
            value={activeRevId || ""}
            onChange={e => setActiveRevId(+e.target.value)}
            style={{ height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, color: "var(--text)", background: "var(--surface)", outline: "none" }}
          >
            {revisions.map(r => (
              <option key={r.RevisionID} value={r.RevisionID}>
                Rev {r.RevisionNumber}: {r.RevisionLabel}{r.IsLocked ? " 🔒" : ""}
              </option>
            ))}
          </select>
          {isLocked && (
            <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--tint-amber)" }}>
              Locked — create new revision to edit
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: "var(--text2)", fontWeight: 500 }}>{checkedCount} of {totalCount} items complete</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: pct === 100 ? "var(--green)" : "var(--cs-blue)" }}>{pct}%</span>
        </div>
        <div style={{ height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "var(--green)" : "var(--cs-blue)", borderRadius: 3, transition: "width 0.3s ease" }} />
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items…"
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", outline: "none", minWidth: 160 }}
        />
        <select value={filterDiscipline} onChange={e => setFilterDiscipline(e.target.value)}
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, background: "var(--surface)", outline: "none" }}>
          {disciplines.map(d => <option key={d}>{d}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, background: "var(--surface)", outline: "none" }}>
          <option value="All">All items</option>
          <option value="Unchecked">Unchecked</option>
          <option value="Checked">Checked</option>
          <option value="Excluded">Excluded</option>
        </select>
        <select value={filterResp} onChange={e => setFilterResp(e.target.value)}
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, background: "var(--surface)", outline: "none" }}>
          <option value="All">All responsibilities</option>
          <option value="(Unassigned)">(Unassigned)</option>
          {respOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        {(search || filterDiscipline !== "All" || filterStatus !== "All" || filterResp !== "All") && (
          <button onClick={() => { setSearch(""); setFilterDiscipline("All"); setFilterStatus("All"); setFilterResp("All"); }}
            style={{ fontSize: 12, color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {/* Grouped checklist */}
      {Object.keys(grouped).length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>No items match your filters.</div>
      ) : (
        Object.entries(grouped).map(([discipline, items]) => (
          <div key={discipline} style={{ marginBottom: 20 }}>
            {/* Discipline header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {discipline}
              </div>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                {items.filter(i => i.IsChecked && !i.IsExcluded).length}/{items.filter(i => !i.IsExcluded).length}
              </div>
            </div>

            {/* Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map(item => (
                <ChecklistItem
                  key={item.ItemID}
                  item={item}
                  isLocked={isLocked}
                  milestones={milestones}
                  respOptions={respOptions}
                  onCheck={(v) => handleCheck(item, v)}
                  onExclude={() => handleExclude(item)}
                  onResponsibility={(v) => handleResponsibility(item, v)}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ChecklistItem({ item, isLocked, milestones, respOptions, onCheck, onExclude, onResponsibility }) {
  const [expanded, setExpanded] = useState(false);
  const [editingResp, setEditingResp] = useState(false);

  const autoHighlight = item.IsAutoChecked && item.IsChecked;
  const rc = respColor(item.Responsibility);

  return (
    <div style={{
      border: `1px solid ${autoHighlight ? "rgba(180,130,0,0.3)" : "var(--cs-border)"}`,
      borderLeft: `3px solid ${item.IsExcluded ? "#d1d5db" : item.IsChecked ? "var(--green)" : autoHighlight ? "#d97706" : "var(--cs-border)"}`,
      borderRadius: "var(--radius)",
      background: item.IsExcluded ? "#fafafa" : autoHighlight ? "var(--tint-amber)" : "#fff",
      opacity: item.IsExcluded ? 0.6 : 1,
      transition: "all 0.15s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px" }}>
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={!!item.IsChecked}
          disabled={isLocked || item.IsExcluded}
          onChange={e => onCheck(e.target.checked)}
          style={{ width: 15, height: 15, cursor: isLocked || item.IsExcluded ? "default" : "pointer", flexShrink: 0, accentColor: "var(--cs-navy)" }}
        />

        {/* Item name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            fontSize: 13,
            color: item.IsExcluded ? "var(--text3)" : "var(--text)",
            textDecoration: item.IsExcluded ? "line-through" : "none",
            fontWeight: item.IsChecked && !item.IsExcluded ? 500 : 400,
          }}>
            {item.ItemName}
          </span>
          {autoHighlight && (
            <span style={{ fontSize: 10, marginLeft: 8, color: "#92400e", fontWeight: 600, padding: "1px 6px", borderRadius: 100, background: "var(--tint-amber)" }}>
              Auto-added by rule
            </span>
          )}
          {item.VendorCategory && (
            <span style={{ fontSize: 10, marginLeft: 6, color: "var(--text3)", padding: "1px 6px", borderRadius: 100, background: "#f3f4f6" }}>
              {item.VendorCategory}
            </span>
          )}
        </div>

        {/* Responsibility — badge that turns into a select on click */}
        {editingResp && !isLocked && !item.IsExcluded ? (
          <select
            autoFocus
            value={item.Responsibility || ""}
            onChange={e => { onResponsibility(e.target.value); setEditingResp(false); }}
            onBlur={() => setEditingResp(false)}
            style={{ height: 24, fontSize: 11, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 4px", background: "var(--surface)", outline: "none", flexShrink: 0 }}
          >
            <option value="">(Unassigned)</option>
            {respOptions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        ) : (
          <button
            onClick={() => !isLocked && !item.IsExcluded && setEditingResp(true)}
            title={isLocked ? "Revision locked" : "Click to change responsibility"}
            style={{
              fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
              padding: "2px 8px", borderRadius: 100, border: "none",
              cursor: isLocked || item.IsExcluded ? "default" : "pointer",
              background: item.Responsibility ? rc.bg : "#fff",
              color: item.Responsibility ? rc.color : "var(--text3)",
              outline: item.Responsibility ? "none" : "1px dashed var(--cs-border)",
            }}
          >
            {item.Responsibility || "+ Responsibility"}
          </button>
        )}

        {/* Milestone badge */}
        {item.MilestoneName && (
          <span style={{ fontSize: 10, color: "var(--text3)", whiteSpace: "nowrap", padding: "2px 6px", borderRadius: 100, background: "#f3f4f6" }}>
            📅 {item.MilestoneName}
          </span>
        )}

        {/* Guidance + exclude */}
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {item.GuidanceNotes && (
            <button onClick={() => setExpanded(e => !e)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text3)", padding: "2px 6px" }}>
              {expanded ? "▲" : "▾"}
            </button>
          )}
          <button
            onClick={onExclude}
            disabled={isLocked}
            title={item.IsExcluded ? "Re-include" : "Mark N/A"}
            style={{ background: "none", border: "none", cursor: isLocked ? "default" : "pointer", fontSize: 11, color: "var(--text3)", padding: "2px 6px" }}
          >
            {item.IsExcluded ? "↩" : "N/A"}
          </button>
        </div>
      </div>

      {/* Guidance notes */}
      {expanded && item.GuidanceNotes && (
        <div style={{ padding: "0 12px 10px 37px", fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>
          {item.GuidanceNotes}
        </div>
      )}
    </div>
  );
}
